import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, Package, ImagePlus, X, Loader2, 
  Trash2, Eye, EyeOff, Palette, Upload, Link as LinkIcon,
  GripVertical, Star, Crown, Wand2, Settings, Plus, Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import ProductImageEditor from '../components/ProductImageEditor';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

const VendorProductEditor = () => {
  const navigate = useNavigate();
  const { productId } = useParams(); // If editing, productId will be set
  const { user, isAuthenticated, isHydrated, forceHydrate } = useAuthStore();
  
  const isEditing = !!productId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [product, setProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: 0,
    images: [],
    is_active: true,
    is_handmade: false,
    color_variants: [],
    shipping_cost: ''
  });
  
  const [newColorVariant, setNewColorVariant] = useState({
    color_name: '',
    color_code: '#000000',
    price_adjustment: 0,
    stock: 0,
    image: ''
  });
  const [imageUrl, setImageUrl] = useState('');
  
  // Image Editor states
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [editingImageUrl, setEditingImageUrl] = useState('');
  const [editingImageFile, setEditingImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('upload'); // 'upload', 'camera', or 'url'
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isGold, setIsGold] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    forceHydrate();
  }, [forceHydrate]);

  useEffect(() => {
    if (!isHydrated) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/vendor-dashboard/products/new');
      return;
    }
    
    if (user?.role !== 'vendor') {
      toast.error('Nur für Verkäufer zugänglich');
      navigate('/');
      return;
    }
    
    loadCategories();
    checkGoldStatus();
    if (isEditing) {
      loadProduct();
    }
  }, [isAuthenticated, user, navigate, isHydrated, productId]);

  const checkGoldStatus = async () => {
    try {
      const response = await axiosInstance.get('/vendor-subscriptions/my-subscription');
      setIsGold(response.data?.is_gold || false);
    } catch (error) {
      console.error('Error checking gold status:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProduct = async () => {
    try {
      const response = await axiosInstance.get(`/vendors/me/products/${productId}`);
      const p = response.data;
      setProduct({
        name: p.name || '',
        description: p.description || '',
        price: p.price?.toString() || '',
        category: p.category || '',
        stock: p.stock || 0,
        images: p.images || [],
        is_active: p.is_active ?? true,
        is_handmade: p.is_handmade ?? false,
        color_variants: p.color_variants || [],
        shipping_cost: p.shipping_cost?.toString() || ''
      });
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Produkt nicht gefunden');
      navigate('/vendor-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!product.name.trim()) {
      toast.error('Bitte geben Sie einen Produktnamen ein');
      return;
    }
    
    if (!product.price || parseFloat(product.price) <= 0) {
      toast.error('Bitte geben Sie einen gültigen Preis ein');
      return;
    }
    
    if (!product.category) {
      toast.error('Bitte wählen Sie eine Kategorie');
      return;
    }
    
    setSaving(true);
    
    try {
      const productData = {
        name: product.name.trim(),
        description: product.description.trim(),
        price: parseFloat(product.price),
        category: product.category,
        stock: parseInt(product.stock) || 0,
        images: product.images,
        is_active: product.is_active,
        is_handmade: product.is_handmade,
        color_variants: product.color_variants,
        shipping_cost: product.shipping_cost ? parseFloat(product.shipping_cost) : null
      };
      
      if (isEditing) {
        await axiosInstance.put(`/vendors/me/products/${productId}`, productData);
        toast.success('Produkt aktualisiert!');
      } else {
        await axiosInstance.post('/vendors/me/products', productData);
        toast.success('Produkt erstellt!');
      }
      
      navigate('/vendor-dashboard');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = () => {
    if (imageUrl.trim() && !product.images.includes(imageUrl.trim())) {
      setProduct({
        ...product,
        images: [...product.images, imageUrl.trim()]
      });
      setImageUrl('');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Open image editor with first file
    const file = files[0];
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(`${file.name}: Nicht unterstütztes Format. Erlaubt: JPG, PNG, GIF, WebP`);
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name}: Datei zu groß (max. 5MB)`);
      return;
    }
    
    setEditingImageFile(file);
    setEditingImageUrl(URL.createObjectURL(file));
    setEditingImageIndex(null);
    setShowImageEditor(true);
    
    // Reset input
    e.target.value = '';
  };

  // Direct camera upload without editor
  const handleCameraCapture = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5MB)');
      return;
    }
    
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axiosInstance.post('/uploads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadResponse.data.url) {
        let imageUrl = uploadResponse.data.url;
        if (imageUrl.startsWith('/api')) {
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
        }

        setProduct(prev => ({
          ...prev,
          images: [...prev.images, imageUrl]
        }));

        toast.success('Foto erfolgreich hinzugefügt!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen des Fotos');
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  // Camera functions - kept for future use but not currently needed
  // Using native capture="environment" instead

  const handleEditExistingImage = (imageUrl, index) => {
    setEditingImageFile(null);
    setEditingImageUrl(imageUrl);
    setEditingImageIndex(index);
    setShowImageEditor(true);
  };

  const handleSaveEditedImage = async (processedImageData) => {
    try {
      // If it's a base64 data URL, we need to upload it
      if (processedImageData.startsWith('data:')) {
        setUploading(true);
        // Convert base64 to blob
        const response = await fetch(processedImageData);
        const blob = await response.blob();
        
        const formData = new FormData();
        formData.append('file', blob, 'processed-image.png');
        
        const uploadResponse = await axiosInstance.post('/uploads/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data?.url) {
          // Make sure we have the full URL
          let imageUrl = uploadResponse.data.url;
          if (imageUrl.startsWith('/api')) {
            imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
          }
          
          const newImages = [...product.images];
          
          if (editingImageIndex !== null) {
            // Replace existing image
            newImages[editingImageIndex] = imageUrl;
          } else {
            // Add new image
            newImages.push(imageUrl);
          }
          
          setProduct({ ...product, images: newImages });
          toast.success('Bild erfolgreich gespeichert!');
        }
        setUploading(false);
      }
    } catch (error) {
      console.error('Error saving edited image:', error);
      toast.error('Fehler beim Speichern des Bildes');
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setProduct({
      ...product,
      images: product.images.filter((_, i) => i !== index)
    });
  };

  // Drag & Drop Image Reordering
  const handleDragStart = (e, index) => {
    if (!isGold) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (!isGold || draggedIndex === null) return;
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!isGold || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...product.images];
    const draggedItem = newImages[draggedIndex];
    
    // Remove from old position
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedItem);
    
    setProduct({ ...product, images: newImages });
    setDraggedIndex(null);
    setDragOverIndex(null);
    toast.success('Bildreihenfolge geändert');
  };

  const moveImageToFirst = (index) => {
    if (index === 0) return;
    const newImages = [...product.images];
    const [removed] = newImages.splice(index, 1);
    newImages.unshift(removed);
    setProduct({ ...product, images: newImages });
    toast.success('Als Hauptbild festgelegt');
  };

  // Drag & Drop Zone for file upload
  const handleDropZoneDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add('border-blue-500', 'bg-blue-50');
    }
  }, []);

  const handleDropZoneDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-blue-500', 'bg-blue-50');
    }
  }, []);

  const handleDropZoneDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-blue-500', 'bg-blue-50');
    }
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a fake event to reuse handleFileUpload
      handleFileUpload({ target: { files } });
    }
  }, [product.images]);

  const handleAddColorVariant = () => {
    if (newColorVariant.color_name.trim()) {
      // Check if color already exists
      const exists = product.color_variants.some(
        v => (typeof v === 'string' ? v : v.color_name).toLowerCase() === newColorVariant.color_name.toLowerCase().trim()
      );
      if (!exists) {
        setProduct({
          ...product,
          color_variants: [...product.color_variants, { ...newColorVariant, color_name: newColorVariant.color_name.trim() }]
        });
        setNewColorVariant({
          color_name: '',
          color_code: '#000000',
          price_adjustment: 0,
          stock: 0,
          image: ''
        });
      }
    }
  };

  const handleUpdateColorVariant = (index, field, value) => {
    const newVariants = [...product.color_variants];
    // Convert string variants to objects if needed
    if (typeof newVariants[index] === 'string') {
      newVariants[index] = { color_name: newVariants[index], color_code: '#000000', price_adjustment: 0, stock: 0, image: '' };
    }
    newVariants[index][field] = value;
    setProduct({ ...product, color_variants: newVariants });
  };

  const handleRemoveColorVariant = (index) => {
    setProduct({
      ...product,
      color_variants: product.color_variants.filter((_, i) => i !== index)
    });
  };

  if (!isHydrated || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 lg:pb-8" style={{ backgroundColor: '#F5EDE4' }}>
      {/* Header - Admin Style */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/vendor-dashboard">
              <Button variant="ghost" size="sm" className="text-[#8B7355] hover:bg-[#F5EDE4]">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-[#5D4E3C]">
                {isEditing ? 'Produkt bearbeiten' : 'Neues Produkt'}
              </h1>
            </div>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="rounded-none"
              style={{ backgroundColor: '#AF9774' }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Header - Grundinformationen */}
          <div className="p-2 px-3 flex items-center gap-2 rounded-none" >
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFFEF5]">
              <Package className="h-4 w-4 text-[#D4B030]" />
            </div>
            <h2 className="text-base font-semibold text-white">Grundinformationen</h2>
          </div>
          
          {/* Basic Info */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="hidden">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Grundinformationen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name" className="block">Produktname&nbsp;*</Label>
                <Input
                  id="name"
                  value={product.name}
                  onChange={(e) => setProduct({...product, name: e.target.value})}
                  placeholder="z.B. Handgemachte Kerze - Lavendel"
                  className="mt-1 w-full"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description" className="block">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={product.description}
                  onChange={(e) => setProduct({...product, description: e.target.value})}
                  placeholder="Beschreiben Sie Ihr Produkt..."
                  className="mt-1 min-h-[120px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price" className="block">Preis&nbsp;(€)&nbsp;*</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={product.price}
                    onChange={(e) => setProduct({...product, price: e.target.value})}
                    placeholder="19.99"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="stock" className="block">Lagerbestand</Label>
                  <Input
                    id="stock"
                    type="number"
                    min="0"
                    value={product.stock}
                    onChange={(e) => setProduct({...product, stock: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category" className="block">Kategorie&nbsp;*</Label>
                  <select
                    id="category"
                    value={product.category}
                    onChange={(e) => setProduct({...product, category: e.target.value})}
                    className="w-full mt-1 border rounded-none px-3 py-2"
                    required
                  >
                    <option value="">Bitte wählen...</option>
                    {categories.map(cat => (
                      <option key={cat.name || cat} value={cat.name || cat}>
                        {cat.name || cat}
                      </option>
                    ))}
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="shipping_cost" className="block">Versandkosten&nbsp;(€)</Label>
                  <Input
                    id="shipping_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={product.shipping_cost}
                    onChange={(e) => setProduct({...product, shipping_cost: e.target.value})}
                    placeholder="Leer = Standard"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section Header - Bilder */}
          <div className="p-2 px-3 flex items-center gap-2 rounded-none" >
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8F0]">
              <ImagePlus className="h-4 w-4 text-[#E89860]" />
            </div>
            <h2 className="text-base font-semibold text-white">Bilder</h2>
            {isGold && (
              <Badge className="ml-2 text-xs" style={{ backgroundColor: '#CBAA00' }}>
                <Crown className="h-3 w-3 mr-1" />
                Drag & Drop
              </Badge>
            )}
          </div>

          {/* Images */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="hidden">
              <CardTitle className="flex items-center gap-2">
                <ImagePlus className="h-5 w-5" />
                Bilder
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Upload Mode Toggle */}
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant={uploadMode === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUploadMode('upload')}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Galerie
                </Button>
                <Button
                  type="button"
                  variant={uploadMode === 'camera' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUploadMode('camera')}
                  className="flex-1"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Kamera
                </Button>
                <Button
                  type="button"
                  variant={uploadMode === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUploadMode('url')}
                  className="flex-1"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  URL
                </Button>
              </div>

              {uploadMode === 'camera' ? (
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCameraCapture}
                    className="hidden"
                    id="camera-capture-vendor"
                  />
                  <label
                    htmlFor="camera-capture-vendor"
                    className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-none cursor-pointer transition-all ${
                      uploading 
                        ? 'bg-gray-100 border-gray-300' 
                        : 'hover:bg-[#FFF8F0] border-[#D4C4B0] hover:border-[#C4A084]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-10 w-10 animate-spin text-[#C4A084] mb-2" />
                        <span className="text-sm text-gray-600">Wird hochgeladen...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="h-10 w-10 text-[#C4A084] mb-2" />
                        <span className="text-sm font-medium text-[#5D4E3C]">Tippen zum Fotografieren</span>
                        <span className="text-sm text-[#8B7355]">Öffnet die Kamera direkt</span>
                      </>
                    )}
                  </label>
                </div>
              ) : uploadMode === 'upload' ? (
                <div className="space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    ref={dropZoneRef}
                    htmlFor="image-upload"
                    onDragOver={handleDropZoneDragOver}
                    onDragLeave={handleDropZoneDragLeave}
                    onDrop={handleDropZoneDrop}
                    className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                      uploading 
                        ? 'bg-gray-100 border-gray-300' 
                        : 'hover:bg-blue-50 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-10 w-10 animate-spin text-blue-500 mb-2" />
                        <span className="text-sm text-gray-600">Wird hochgeladen...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <span className="text-sm font-medium text-gray-600">Bilder hierher ziehen</span>
                        <span className="text-sm text-gray-500">oder klicken zum Auswählen</span>
                        <span className="text-xs text-gray-400 mt-2">JPG, PNG, GIF, WebP (max. 5MB pro Bild)</span>
                      </>
                    )}
                  </label>
                </div>
              ) : uploadMode === 'url' ? (
                <div className="flex gap-2">
                  <Input
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://... Bild-URL einfügen"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImage())}
                  />
                  <Button type="button" onClick={handleAddImage} variant="outline">
                    Hinzufügen
                  </Button>
                </div>
              ) : null}
              
              {/* Gold Feature Hint */}
              {!isGold && product.images.length > 1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-3">
                  <Crown className="h-5 w-5 text-yellow-600 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800">Gold-Feature: Bilder per Drag & Drop sortieren</p>
                    <p className="text-yellow-700">Upgraden Sie auf Gold um die Reihenfolge Ihrer Bilder anzupassen.</p>
                  </div>
                </div>
              )}
              
              {product.images.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {product.images.map((img, index) => (
                    <div 
                      key={index} 
                      draggable={isGold}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`relative group rounded-lg transition-all ${
                        isGold ? 'cursor-grab active:cursor-grabbing' : ''
                      } ${
                        dragOverIndex === index && draggedIndex !== index
                          ? 'ring-2 ring-blue-500 ring-offset-2'
                          : ''
                      } ${
                        draggedIndex === index ? 'opacity-50' : ''
                      }`}
                    >
                      {/* Drag Handle for Gold users */}
                      {isGold && (
                        <div className="absolute top-2 left-2 z-10 bg-white/90 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      
                      <img
                        src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                        alt={`Bild ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg border"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100%" height="100%"/><text x="50%" y="50%" fill="%239ca3af" dominant-baseline="middle" text-anchor="middle" font-size="12">Bild fehlt</text></svg>';
                        }}
                      />
                      
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => handleEditExistingImage(
                            img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img, 
                            index
                          )}
                          className="bg-[#AF9774] text-white rounded-full p-1.5 hover:bg-[#9A8569] transition-colors"
                          title="Bild bearbeiten (Filter & Hintergrund)"
                        >
                          <Wand2 className="h-3 w-3" />
                        </button>
                        {index !== 0 && (
                          <button
                            type="button"
                            onClick={() => moveImageToFirst(index)}
                            className="bg-yellow-500 text-white rounded-full p-1.5 hover:bg-yellow-600 transition-colors"
                            title="Als Hauptbild festlegen"
                          >
                            <Star className="h-3 w-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                          title="Bild entfernen"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      
                      {/* Main image badge */}
                      {index === 0 && (
                        <Badge className="absolute bottom-2 left-2 bg-blue-500">
                          <Star className="h-3 w-3 mr-1" />
                          Hauptbild
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                  <ImagePlus className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Bilder hinzugefügt</p>
                  <p className="text-xs">Ziehen Sie Bilder hierher oder klicken Sie zum Hochladen</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Header - Farbvarianten */}
          <div className="p-2 px-3 flex items-center gap-2 rounded-none" >
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F8F0FF]">
              <Palette className="h-4 w-4 text-[#A070D0]" />
            </div>
            <h2 className="text-base font-semibold text-white">Farbvarianten (optional)</h2>
          </div>

          {/* Color Variants */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="hidden">
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Farbvarianten (optional)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Add new variant form */}
              <div className="p-4 rounded-none space-y-3" style={{ backgroundColor: '#F5EDE4' }}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[#5D4E3C]">Neue Farbvariante hinzufügen:</p>
                  <Button 
                    type="button" 
                    onClick={handleAddColorVariant} 
                    variant="outline" 
                    className="rounded-none border-[#C4A084] text-[#AF9774] hover:bg-[#F5EDE4]"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#8B7355] block">Farbname&nbsp;*</Label>
                    <Input
                      value={newColorVariant.color_name}
                      onChange={(e) => setNewColorVariant({...newColorVariant, color_name: e.target.value})}
                      placeholder="z.B. Rot, Blau, Grün"
                      className="mt-1 rounded-none border-[#D4C4B0] focus:ring-[#C4A084]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#8B7355] block">Farbcode&nbsp;(optional)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        value={newColorVariant.color_code}
                        onChange={(e) => setNewColorVariant({...newColorVariant, color_code: e.target.value})}
                        className="w-14 p-1 h-9 rounded-none border-[#D4C4B0]"
                      />
                      <Input
                        value={newColorVariant.color_code}
                        onChange={(e) => setNewColorVariant({...newColorVariant, color_code: e.target.value})}
                        placeholder="#000000"
                        className="flex-1 rounded-none border-[#D4C4B0]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8B7355] block">Preisanpassung&nbsp;(€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newColorVariant.price_adjustment}
                      onChange={(e) => setNewColorVariant({...newColorVariant, price_adjustment: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="mt-1 rounded-none border-[#D4C4B0]"
                    />
                    <p className="text-xs text-[#8B7355] mt-0.5">+ für Aufpreis, - für Rabatt</p>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8B7355] block">Lagerbestand&nbsp;*</Label>
                    <Input
                      type="number"
                      value={newColorVariant.stock}
                      onChange={(e) => setNewColorVariant({...newColorVariant, stock: parseInt(e.target.value) || 0})}
                      placeholder="0"
                      className="mt-1 rounded-none border-[#D4C4B0]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Existing variants */}
              {product.color_variants.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-[#5D4E3C]">Vorhandene Farbvarianten:</p>
                  {product.color_variants.map((variant, index) => {
                    // Support both string and object formats
                    const variantObj = typeof variant === 'string' 
                      ? { color_name: variant, color_code: '#000000', price_adjustment: 0, stock: 0, image: '' }
                      : variant;
                    
                    return (
                      <div key={index} className="border border-[#D4C4B0] rounded-none p-4 space-y-3 bg-white">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded-full border border-[#D4C4B0]"
                              style={{ backgroundColor: variantObj.color_code }}
                            />
                            <span className="font-medium text-[#5D4E3C]">Farbe {index + 1}: {variantObj.color_name}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveColorVariant(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            Entfernen
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-[#8B7355] block">Farbname&nbsp;*</Label>
                            <Input
                              value={variantObj.color_name}
                              onChange={(e) => handleUpdateColorVariant(index, 'color_name', e.target.value)}
                              className="mt-1 rounded-none border-[#D4C4B0]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#8B7355] block">Farbcode&nbsp;(optional)</Label>
                            <div className="flex gap-2 mt-1">
                              <Input
                                type="color"
                                value={variantObj.color_code}
                                onChange={(e) => handleUpdateColorVariant(index, 'color_code', e.target.value)}
                                className="w-14 p-1 h-9 rounded-none border-[#D4C4B0]"
                              />
                              <Input
                                value={variantObj.color_code}
                                onChange={(e) => handleUpdateColorVariant(index, 'color_code', e.target.value)}
                                className="flex-1 rounded-none border-[#D4C4B0]"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-[#8B7355] block">Preisanpassung&nbsp;(€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={variantObj.price_adjustment}
                              onChange={(e) => handleUpdateColorVariant(index, 'price_adjustment', parseFloat(e.target.value) || 0)}
                              className="mt-1 rounded-none border-[#D4C4B0]"
                            />
                            <p className="text-xs text-[#8B7355] mt-0.5">+ Aufpreis, - Rabatt</p>
                          </div>
                          <div>
                            <Label className="text-xs text-[#8B7355] block">Lagerbestand</Label>
                            <Input
                              type="number"
                              value={variantObj.stock}
                              onChange={(e) => handleUpdateColorVariant(index, 'stock', parseInt(e.target.value) || 0)}
                              className="mt-1 rounded-none border-[#D4C4B0]"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Header - Einstellungen */}
          <div className="p-2 px-3 flex items-center gap-2 rounded-none" >
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FAF8F5]">
              <Settings className="h-4 w-4 text-[#A09080]" />
            </div>
            <h2 className="text-base font-semibold text-white">Einstellungen</h2>
          </div>

          {/* Settings */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="hidden">
              <CardTitle>Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="flex items-center justify-between p-3 rounded-none" style={{ backgroundColor: '#F5EDE4' }}>
                <div>
                  <Label className="text-[#5D4E3C]">Produkt aktiv</Label>
                  <p className="text-sm text-[#8B7355]">Aktive Produkte sind für Kunden sichtbar</p>
                </div>
                <Switch
                  checked={product.is_active}
                  onCheckedChange={(checked) => setProduct({...product, is_active: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-none" style={{ backgroundColor: '#F5EDE4' }}>
                <div>
                  <Label className="text-[#5D4E3C]">Handgemacht</Label>
                  <p className="text-sm text-[#8B7355]">Zeigt ein "Handgemacht" Badge an</p>
                </div>
                <Switch
                  checked={product.is_handmade}
                  onCheckedChange={(checked) => setProduct({...product, is_handmade: checked})}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-4 justify-end">
            <Link to="/vendor-dashboard">
              <Button type="button" variant="outline" className="rounded-none border-[#C4A084] text-[#AF9774]">
                Abbrechen
              </Button>
            </Link>
            <Button 
              type="submit" 
              disabled={saving}
              className="rounded-none"
              style={{ backgroundColor: '#AF9774' }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditing ? 'Änderungen speichern' : 'Produkt erstellen'}
            </Button>
          </div>
        </form>
      </main>
      
      {/* Product Image Editor */}
      <ProductImageEditor
        isOpen={showImageEditor}
        onClose={() => {
          setShowImageEditor(false);
          setEditingImageFile(null);
          setEditingImageUrl('');
          setEditingImageIndex(null);
        }}
        imageFile={editingImageFile}
        imageUrl={editingImageUrl}
        onSave={handleSaveEditedImage}
      />
    </div>
  );
};

export default VendorProductEditor;
